import { ThunkDispatch } from 'redux-thunk';
import * as Sentry from '@sentry/react';

import { API_CONFIG } from 'shared/constants/constants';
import { Action } from 'shared/interface';
import AuthService from 'shared/services/auth.service';
import HttpService from 'shared/services/http.service';
import { refreshLanguage } from 'shared/util/localization';
import { normalizeLanguage } from 'shared/util/translation/language';
import { createAction } from 'shared/util/utility';
import * as actionTypes from 'store/action-types';
import { LoginResponse, UserLoginData, UserProfileResponse } from '../interface/login';

const login = (data: UserLoginData, dispatch: (action: Action) => void) => {
	dispatch(createAction(actionTypes.AUTH_INIT));
	HttpService.post(
		API_CONFIG.path.login,
		data,
		{},
		{
			isAccessTokenRequire: false
		}
	)
		.then((response: LoginResponse) => {
			const normalizedLanguage = normalizeLanguage(response.sprache);
			// if (['production', 'staging', 'development'].includes(ENVIRONMENT)) {
			// 	window.__insp.push([
			// 		'tagSession',
			// 		{
			// 			email: response.email,
			// 			environment: ENVIRONMENT
			// 		}
			// 	]);
			// }
			if (['production', 'staging', 'development', 'local'].includes(process.env.REACT_APP_ENVIRONMENT as string)) {
				if (response.benutzer_id) {
					Sentry.setUser({
						id: response.benutzer_id,
						email: response.email,
						username: response.vorname + ' ' + response.nachname
					});
				}
			}
			localStorage.setItem('isAnnouncement', JSON.stringify(1));
			AuthService.setAuthData(response);
			AuthService.setMatfoxxSettings(response.matfoxx_settings);
			AuthService.setDateFormate(response.date_format);
			AuthService.setExportDateFormat(response.export_date_format || response.date_format);
			AuthService.setChartConfig({ chart_type: response.chart_type, chart_color: response.chart_color });
			AuthService.setDecimalSeparator(response.decimal_separator);
			localStorage.setItem('recordPerPage', JSON.stringify(response.page_limit));
			AuthService.setAvatar(response.avatar);
			AuthService.setAnalyticsSessionStore({
				endTime: new Date().toISOString(),
				startTime: new Date().toISOString(),
				pageVisitsStreamId: '',
				sessionStreamUserId: ''
			});
			refreshLanguage(normalizedLanguage);
			dispatch(createAction(actionTypes.AUTH_SUCCESS, response));
			dispatch(
				createAction(actionTypes.UPDATE_SITE_CONFIG, {
					language: normalizedLanguage,
					dateFormat: response.date_format,
					exportDateFormat: response.export_date_format || response.date_format,
					matfoxxConfig: response.matfoxx_settings,
					recordPerPage: response.page_limit,
					admin: response.admin,
					chartView: response.chart_view,
					currencyView: response.currency_list_view,
					chartConfig: { chart_type: response.chart_type, chart_color: response.chart_color },
					decimal_separator: response.decimal_separator,
					avatar: response.avatar
				})
			);
		})
		.catch(() => {
			dispatch(createAction(actionTypes.AUTH_FAIL));
		});
};

const getProfile = () => (dispatch: ThunkDispatch<Record<string, never>, Record<string, never>, Action>) => {
	dispatch(createAction(actionTypes.GET_PROFILE_INIT));
	HttpService.get(API_CONFIG.path.profile)
		.then((profileResponse: UserProfileResponse) => {
			AuthService.setUserData(profileResponse);
			dispatch(createAction(actionTypes.GET_PROFILE_SUCCESS, profileResponse));
		})
		.catch(() => {
			dispatch(createAction(actionTypes.GET_PROFILE_FAIL));
		});
};

const logout = (dispatch: ThunkDispatch<Record<string, never>, Record<string, never>, Action>) => {
	dispatch(createAction(actionTypes.AUTH_LOGOUT_INIT));
	HttpService.put(API_CONFIG.path.logout)
		.then(() => {
			dispatch(createAction(actionTypes.AUTH_LOGOUT_SUCCESS));
			localStorage.clear();
			sessionStorage.clear();
		})
		.catch(() => {
			dispatch(createAction(actionTypes.AUTH_LOGOUT_FAIL));
		});
};

export { login, logout, getProfile };
